import React, { useEffect } from 'react'
import { useParams } from 'react-router'
import { Box, Divider, Group, Pagination, Pill, Stack, Text, Title } from '@mantine/core'
import { usePricingVersionCache, useReviewVersionMatches } from '../../../../../hooks/global-job-matching'
import { UseMutationResult } from 'react-query'
import { FILTER, textOperators, numberOperators, FieldFilter } from '../../../../../constants/filter-operators'
import { ReviewMatchesTable } from './ReviewMatchesTable'

type ReviewProps = {
  runTheRulesMutation: UseMutationResult
  polling: boolean
  debugEnabled: boolean
}

export const Review = ({ runTheRulesMutation, polling, debugEnabled }: ReviewProps) => {
  const params = useParams<{ id: string}>()
  const version = usePricingVersionCache(params.id)
  const { data: reviewMatches, isLoading: matchesLoading, isPreviousData, sort, setSort, page, setPage, limit, setFilters, filters } = useReviewVersionMatches(params.id)

  useEffect(() => {
    if (version.requiresRun) runTheRulesMutation.mutate(params.id)
  }, [version.requiresRun])

  const getFilterLabel = (filter: FieldFilter) => {
    const operatorLabel = [...textOperators, ...numberOperators].find(op => op.value === filter.operator)?.label?.toLowerCase()
    const filterValue = [FILTER.IS_NULL, FILTER.IS_NOT_NULL].includes(filter.operator) ? '' : filter.value
    return <Text><Text span fw='bold'>{filter.label} </Text>{operatorLabel} <Text span fw='bold'>{filterValue}</Text></Text>
  }

  return (
    <Box style={{ overflow: 'none' }}>
      <Stack p='xl'>
        <Title order={2} fw={600}>Review Matches</Title>
        <Text size='lg'>Review the matches made from your specified ruleset</Text>
        { !!filters.length &&
        <Group>
          <Text size='lg'>Filters: </Text>
          {filters.map((f, idx) => <Pill c='blue' size='xl' withRemoveButton onRemove={() => setFilters(prev => prev.filter((_, fIdx) => fIdx !== idx))}>{getFilterLabel(f)}</Pill>)}
        </Group>
        }
      </Stack>
      <ReviewMatchesTable
        matches={reviewMatches?.matches}
        matchesLoading={matchesLoading}
        isPreviousData={isPreviousData}
        sort={sort}
        filters={filters}
        setFilters={setFilters}
        setSort={setSort}
        setPage={setPage}
        runTheRulesMutation={runTheRulesMutation}
        polling={polling}
        debugEnabled={debugEnabled}
      />
      <Divider my='xl' />
      { reviewMatches?.matchCount > 50 &&
        <Group justify='center'>
        <Pagination total={(reviewMatches?.matchCount/limit) + 1 || 0} value={page} onChange={setPage} />
      </Group> }
    </Box>
  )
}

